import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const parsePrice = (value) => {
  if (!value || value?.toString()?.trim() === '') return '';
  const str = value?.toString()?.toLowerCase()?.trim();
  // Remove commas and dollar signs
  const cleaned = str?.replace(/[$,]/g, '');
  // Match number + optional k/m/million/thousand
  const match = cleaned?.match(/^([\d.]+)\s*(k|thousand|m|million)?$/);
  if (!match) return '';
  const num = parseFloat(match?.[1]);
  if (isNaN(num)) return '';
  const suffix = match?.[2];
  if (suffix === 'k' || suffix === 'thousand') return Math.round(num * 1000);
  if (suffix === 'm' || suffix === 'million') return Math.round(num * 1000000);
  return Math.round(num);
};

const formatDisplayPrice = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  if (num >= 1000000) return `${(num / 1000000)?.toFixed(num % 1000000 === 0 ? 0 : 1)}M`;
  if (num >= 1000) return `${(num / 1000)?.toFixed(num % 1000 === 0 ? 0 : 1)}k`;
  return num?.toString();
};

const SearchFilters = ({ filters, onFiltersChange, onSearch }) => {
  const navigate = useNavigate();
  const [minPriceText, setMinPriceText] = useState(
    filters?.minPrice ? formatDisplayPrice(filters?.minPrice) : ''
  );
  const [maxPriceText, setMaxPriceText] = useState(
    filters?.maxPrice ? formatDisplayPrice(filters?.maxPrice) : ''
  );

  const modes = [
    { value: 'buy', label: 'Buy' },
    { value: 'rent', label: 'Rent' },
    { value: 'sell', label: 'Sell' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'off-market', label: 'Off-Market' }
  ];

  const handleModeClick = (modeValue) => {
    if (modeValue === 'sell') {
      navigate('/list-your-property-landing');
    } else {
      onFiltersChange({ ...filters, mode: modeValue });
    }
  };

  const handleMinPriceBlur = () => {
    const parsed = parsePrice(minPriceText);
    if (parsed !== '') {
      setMinPriceText(formatDisplayPrice(parsed));
      onFiltersChange({ ...filters, minPrice: parsed });
    } else if (minPriceText?.trim() === '') {
      onFiltersChange({ ...filters, minPrice: '' });
    }
  };

  const handleMaxPriceBlur = () => {
    const parsed = parsePrice(maxPriceText);
    if (parsed !== '') {
      setMaxPriceText(formatDisplayPrice(parsed));
      onFiltersChange({ ...filters, maxPrice: parsed });
    } else if (maxPriceText?.trim() === '') {
      onFiltersChange({ ...filters, maxPrice: '' });
    }
  };

  const handleSearch = () => {
    // Commit any pending price values before searching
    const parsedMin = parsePrice(minPriceText);
    const parsedMax = parsePrice(maxPriceText);
    const updatedFilters = {
      ...filters,
      minPrice: parsedMin !== '' ? parsedMin : (minPriceText?.trim() === '' ? '' : filters?.minPrice),
      maxPrice: parsedMax !== '' ? parsedMax : (maxPriceText?.trim() === '' ? '' : filters?.maxPrice),
    };
    if (parsedMin !== '') setMinPriceText(formatDisplayPrice(parsedMin));
    if (parsedMax !== '') setMaxPriceText(formatDisplayPrice(parsedMax));
    onFiltersChange(updatedFilters);
    if (onSearch) onSearch(updatedFilters);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') handleSearch();
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 mb-6">
      <div className="flex flex-wrap gap-2 mb-4">
        {modes?.map((mode) => (
          <Button
            key={mode?.value}
            variant={filters?.mode === mode?.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeClick(mode?.value)}
          >
            {mode?.label}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Input
          type="text"
          placeholder="Suburb or address"
          value={filters?.query}
          onChange={(e) => onFiltersChange({ ...filters, query: e?.target?.value })}
          onKeyDown={handleKeyDown}
        />

        <div className="flex gap-2">
          <Input
            type="text"
            placeholder="Min e.g. 750k"
            value={minPriceText}
            onChange={(e) => setMinPriceText(e?.target?.value)}
            onBlur={handleMinPriceBlur}
            onKeyDown={handleKeyDown}
          />
          <Input
            type="text"
            placeholder="Max e.g. 1M"
            value={maxPriceText}
            onChange={(e) => setMaxPriceText(e?.target?.value)}
            onBlur={handleMaxPriceBlur}
            onKeyDown={handleKeyDown}
          />
        </div>

        <Button
          variant="default"
          size="default"
          onClick={handleSearch}
          className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          Search Properties
        </Button>
      </div>
    </div>
  );
};

export default SearchFilters;